import React, { useRef, useState } from "react";
import { useDispatch } from "react-redux";

import PageTitle from "../../../layouts/PageTitle";
import {
  appApi,
  useGetNcmpUploadsQuery,
} from "../../../../store/appApi";
import {
  usePartnerCode,
  PartnerPicker,
  panelClass,
  fieldClass,
  labelClass,
  btnPrimary,
  fmtDate,
  fmtDateInput,
} from "./config";
import { uploadComplaintSheet } from "../../../../services/netplusComplaints";
import { uiToast } from "../customerStatus/ui";

const errMsg = (e) => e?.response?.data?.message || "Upload failed.";

const NcmpUpload = () => {
  const dispatch = useDispatch();
  const [partnerCode, setPartnerCode] = usePartnerCode();
  const [snapshotDate, setSnapshotDate] = useState(fmtDateInput());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const { data: uploadsData } = useGetNcmpUploadsQuery({ partnerCode });
  const uploads = uploadsData?.uploads || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      uiToast({ type: "warning", title: "Choose a .csv or .xlsx file first." });
      return;
    }
    try {
      setBusy(true);
      setResult(null);
      const { data } = await uploadComplaintSheet({
        file,
        snapshotDate,
        partnerCode,
      });
      setResult(data?.summary || null);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      dispatch(appApi.util.invalidateTags(["NetplusComplaints"]));
      uiToast({ type: "success", title: "Complaints sheet processed" });
    } catch (err) {
      uiToast({ type: "error", title: "Could not process sheet", message: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageTitle activeMenu="Upload" motherMenu="Complaints" />

      <section className={`${panelClass} mb-6`}>
        <div className="border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Upload daily complaints sheet
          </h2>
          <p className="mt-0.5 text-xs text-gray-400">
            Columns matched (any case): Ticket No · Date · Name · Address ·
            Contact No · Status. Rows are matched across days on Ticket No.
          </p>
        </div>

        <form onSubmit={submit} className="grid gap-4 px-5 py-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <span className={labelClass}>Partner</span>
            <PartnerPicker value={partnerCode} onChange={setPartnerCode} />
          </div>
          <div>
            <span className={labelClass}>Sheet date</span>
            <input
              type="date"
              className={`${fieldClass}`}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              max={fmtDateInput()}
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
            />
          </div>
          <div className="sm:col-span-2">
            <span className={labelClass}>File (.csv / .xlsx)</span>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className={`${fieldClass}`}
              style={{ backgroundColor: "#fff", color: "#1f2937" }}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="sm:col-span-2 lg:col-span-4">
            <button type="submit" className={btnPrimary} disabled={busy}>
              {busy ? "Processing…" : "Upload & match"}
            </button>
          </div>
        </form>

        {result && (
          <div className="border-t border-gray-100 px-5 py-4 text-sm">
            <div className="flex flex-wrap gap-4">
              {[
                ["Rows parsed", result.rowsParsed],
                ["Rows skipped", result.rowsSkipped],
                ["New", result.counts?.created],
                ["Status changed", result.counts?.statusChanged],
                ["Closed", result.counts?.closed],
                ["Re-opened", result.counts?.reopened],
                ["Unchanged", result.counts?.unchanged],
              ].map(([k, v]) => (
                <div key={k} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-center">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {k}
                  </div>
                  <div className="mt-0.5 text-lg font-semibold tabular-nums text-gray-800">
                    {v ?? 0}
                  </div>
                </div>
              ))}
            </div>
            {(result.warnings || []).length > 0 && (
              <ul className="mt-3 list-disc space-y-0.5 pl-5 text-xs text-amber-700">
                {result.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className={panelClass}>
        <div className="border-b border-gray-100 px-5 py-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Recent uploads
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Uploaded</th>
                <th className="px-4 py-3 text-left">Sheet date</th>
                <th className="px-4 py-3 text-left">File</th>
                <th className="px-4 py-3 text-center">Parsed</th>
                <th className="px-4 py-3 text-center">New</th>
                <th className="px-4 py-3 text-center">Changed</th>
                <th className="px-4 py-3 text-left">By</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {uploads.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    No uploads yet for {partnerCode}.
                  </td>
                </tr>
              ) : (
                uploads.map((u) => (
                  <tr key={u._id} className="hover:bg-cyan-50/30">
                    <td className="px-4 py-2.5 text-gray-600">
                      {fmtDate(u.createdAt)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {fmtDate(u.snapshotDate)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      {u.originalFileName || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums">
                      {u.rowsParsed}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums text-emerald-700">
                      {u.counts?.created ?? 0}
                    </td>
                    <td className="px-4 py-2.5 text-center tabular-nums text-amber-700">
                      {u.counts?.statusChanged ?? 0}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-gray-500">
                      {u.uploadedBy?.name || "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

export default NcmpUpload;
